import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Sidebar,
  Icon,
  Form,
  Segment,
} from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import parseSpreadsheetFile, {
  USER_COLUMN_MAP,
  GROUP_COLUMN_MAP,
} from 'helpers/parseSpreadsheetFile';
import './index.scss';
import {
  creditGroup,
  creditUser,
  creditBulkUsers,
  creditBulkGroups,
} from 'redux/actions/wallets/credit';
import notify from 'helpers/notificationHelper';
import { ERROR } from 'constants/notificationStatus';

import CreditOrganizationForm from 'components/Wallet/Credit/CreditOrganizationForm';
import CreditGroupForm from 'components/Wallet/Credit/CreditGroupForm';
import CreditUserForm from 'components/Wallet/Credit/CreditUserForm';
import ConfirmPinModal from 'components/common/modals/ConfirmPinModal';
import {
  CREDIT_SOURCE,
  CREDIT_CHANNEL,
} from 'constants/walletCreditDefaults';
import getWallets from 'redux/actions/wallets/getWallets';
import isParentOrganization from 'helpers/isParentOrganization';

const CreditSidebar = ({
  filterOn,
  setActionType,
  currentOrgId,
  groupId,
  userId,
}) => {
  const [form, setForm] = useState({ source: 'float_balance' });
  const [file, setFile] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [userPin, setUserPin] = useState(null);
  const [pinError, setPinError] = useState(null);
  const [preparedAction, setPreparedAction] = useState(null);
  const dispatch = useDispatch();

  const { loading } = useSelector(
    ({ wallets: { creditAgent } }) => creditAgent,
  );

  const { items: walletItems = [] } = useSelector(
    ({ wallets: { wallets } }) => wallets,
  );

  const isCurrentOrgParent = isParentOrganization();

  useEffect(() => {
    if (walletItems.length === 0 && filterOn) {
      if (isCurrentOrgParent) {
        getWallets()(dispatch);
      } else {
        getWallets(currentOrgId)(dispatch);
      }
    }
  }, [isCurrentOrgParent, filterOn]);

  const handleChange = (name, value) => {
    if (name === 'filter') {
      setForm({ filter: value, source: form.source });
      return;
    }
    if (name === 'bulk') {
      setForm({
        filter: form.filter,
        source: form.source,
        [name]: value,
      });

      setFile({});

      return;
    }
    setForm({ ...form, [name]: value });
  };

  const resetPinState = () => {
    setOpenModal(false);
    setUserPin(null);
    setPinError(null);
    setPreparedAction(null);
  };

  const prepareSubmission = async () => {
    if (userId) {
      setPreparedAction({
        action: creditUser,
        data: {
          ...form,
          organization_id: currentOrgId,
          source: CREDIT_SOURCE,
          channel: CREDIT_CHANNEL,
        },
        id: userId,
      });
      return setOpenModal(true);
    }

    if (groupId) {
      setPreparedAction({
        action: creditGroup,
        data: {
          ...form,
          organization_id: currentOrgId,
          source: CREDIT_SOURCE,
          channel: CREDIT_CHANNEL,
        },
        id: groupId,
      });
      return setOpenModal(true);
    }

    if (form.filter === 'user' && !form.bulk) {
      setPreparedAction({
        action: creditUser,
        data: {
          ...form,
          organization_id: currentOrgId,
          channel: CREDIT_CHANNEL,
        },
        id: form.user,
      });
      return setOpenModal(true);
    }

    if (form.filter === 'group' && !form.bulk) {
      setPreparedAction({
        action: creditGroup,
        data: {
          ...form,
          organization_id: currentOrgId,
          channel: CREDIT_CHANNEL,
        },
        id: form.group,
      });
      return setOpenModal(true);
    }

    if (form.filter === 'user' && form.bulk) {
      try {
        const users = await parseSpreadsheetFile(
          file,
          USER_COLUMN_MAP,
        );
        setPreparedAction({
          action: creditBulkUsers,
          data: {
            ...form,
            users,
            organization_id: currentOrgId,
            channel: CREDIT_CHANNEL,
          },
          isBulk: true,
        });
        return setOpenModal(true);
      } catch (err) {
        return notify({ message: err.message, status: ERROR });
      }
    }

    if (form.filter === 'group' && form.bulk) {
      try {
        const groups = await parseSpreadsheetFile(
          file,
          GROUP_COLUMN_MAP,
        );
        setPreparedAction({
          action: creditBulkGroups,
          data: {
            ...form,
            groups,
            organization_id: currentOrgId,
            channel: CREDIT_CHANNEL,
          },
          isBulk: true,
        });
        return setOpenModal(true);
      } catch (err) {
        return notify({ message: err.message, status: ERROR });
      }
    }

    return null;
  };

  const handleConfirmSubmit = () => {
    if (!preparedAction) return;
    setPinError(null);

    const { action, data, id, isBulk } = preparedAction;
    const dataWithPin = { ...data, password: userPin };

    const callbacks = {
      onSuccessCallback: () => {
        resetPinState();
      },
      onFailureCallback: error => {
        setPinError(error);
      },
    };

    if (isBulk) {
      action(dataWithPin, callbacks)(dispatch);
    } else {
      action(dataWithPin, id, callbacks)(dispatch);
    }
  };

  return (
    <Sidebar
      className="sidebar scrollable"
      as={Segment}
      direction="right"
      animation="overlay"
      visible={filterOn}
      width="wide"
    >
      <div className="padding">
        <div className="header">
          <div
            tabIndex="-1"
            role="button"
            onKeyDown={() => null}
            className="back"
            onClick={() => {
              resetPinState();
              setActionType(null);
            }}
          >
            <Icon name="arrow left" fitted size="large" />
          </div>
          <div className="title titleText">
            <h2>Credit</h2>
          </div>
        </div>
      </div>
      <div className="padding">
        <Form onSubmit={prepareSubmission} loading={loading}>
          {userId && (
            <CreditUserForm
              form={form}
              onChange={handleChange}
              walletItems={walletItems}
            />
          )}
          {!userId && groupId && (
            <CreditGroupForm
              form={form}
              onChange={handleChange}
              walletItems={walletItems}
            />
          )}
          {!userId && !groupId && (
            <CreditOrganizationForm
              form={form}
              onChange={handleChange}
              onFileChange={setFile}
              file={file}
              currentOrgId={currentOrgId}
              walletItems={walletItems}
            />
          )}
          <Button
            primary
            className="sidebar__submit-btn"
            floated="right"
            size="medium"
            content="Initiate"
            type="submit"
          />
        </Form>
        <ConfirmPinModal
          content="Enter your PIN to confirm the credit operation."
          openModal={openModal}
          setOpenModal={setOpenModal}
          userPin={userPin}
          setUserPin={setUserPin}
          hasComplexSubmitAction
          handleSubmit={handleConfirmSubmit}
          loading={loading}
          error={pinError}
        />
      </div>
    </Sidebar>
  );
};

CreditSidebar.defaultProps = {
  groupId: null,
  userId: null,
};

CreditSidebar.propTypes = {
  filterOn: PropTypes.bool.isRequired,
  setActionType: PropTypes.func.isRequired,
  currentOrgId: PropTypes.number.isRequired,
  groupId: PropTypes.number,
  userId: PropTypes.number,
};

export default CreditSidebar;
